import axios, { AxiosResponse } from 'axios';
import { apiConfig } from './config';
import { translate } from '../components/basic/text';
import { parseContentDisposition } from '../helpers/content-disposition-parser';
import { ApiError, CallbackFunc, RequestType, ApiData, ApiType, DownloadResponse } from './types';

const withCredentials = true;

const blockedUrls: Set<string> = new Set();
const lastRequestInfo: RequestType = { url: '', payload: {}, headers: {}, method: 'post' };

let onUnauthorizeHandler: CallbackFunc | null = null;
let onForbiddenHandler: CallbackFunc | null = null;

const genericResponseHandler = (response: AxiosResponse): any => {
    const { data } = response;
    if (!data) {
        throw new Error('invalid response!');
    }

    if (!data.isSuccess) {
        if (data.errorMessage === '401' || data.errorCode === '401') {
            throw newError(translate('401-unauthorized-access'), '401', 200);
        }
        if (data.errorMessage === '403' || data.errorCode === '403') {
            throw newError(translate('403-forbidden-access'), '403', 200);
        }
        throw newError(data.errorMessage, 'error', 200, null, data.errorParams);
    }

    return data.result;
};

function newError(
    message: string,
    name: string,
    httpStatus?: number,
    stack?: any,
    errorParams?: any
): ApiError {
    const error = new Error(message) as ApiError;

    if (name) error.name = name;
    if (httpStatus) error.httpStatus = httpStatus;
    if (errorParams) error.errorParams = errorParams;

    error.stack = stack ?? undefined;

    return error;
}

function translateError(ex: any): never {
    if (!ex) {
        throw newError(translate('unknown-error'), 'Unknown');
    }

    if (ex.response) {
        const { status } = ex.response;
        const errorMap: Record<number, { message: string; code: string }> = {
            401: { message: '401-unauthorized-access', code: '401' },
            403: { message: '403-forbidden-access', code: '403' },
            404: { message: '404-resource-not-found', code: '404' },
            405: { message: '405-method-not-allowed', code: '405' },
            500: { message: '500-server-error', code: '500' },
        };

        if (errorMap[status]) {
            const { message, code } = errorMap[status];
            throw newError(translate(message), code, status);
        }
    }

    if (ex instanceof Error && ['401', '403'].includes(ex.name)) {
        throw ex;
    }

    const message = ex.message ?? ex.toString?.() ?? ex;
    const { stack, httpStatus } = ex;

    if (message === 'Network Error') {
        throw newError(translate('service-is-not-available'), 'NetworkError', httpStatus, stack);
    }

    if (message.startsWith("Failed to execute 'open' on 'XMLHttpRequest': Invalid URL")) {
        throw newError(translate('invalid-url'), 'InvalidUrl', httpStatus, stack);
    }

    throw newError(translate(message, ex.errorParams), 'Error', httpStatus);
}

async function callAxios(
    method: string,
    url: string,
    data?: any,
    headers?: any,
    token?: string | null,
    onProgress?: (percent: number) => void
): Promise<any> {
    const authToken = token ?? apiData.token;
    const requestHeaders = { ...headers, authorization: `Bearer ${authToken}` };

    try {
        const response = await axios({
            url,
            data,
            withCredentials,
            method,
            headers: requestHeaders,
            onUploadProgress: (progressEvent: any) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                onProgress?.(percentCompleted);
            },
        });
        return genericResponseHandler(response);
    } catch (error) {
        translateError(error);
    }
}

function dispatchUnauthorized(ex: ApiError): void {
    onUnauthorizeHandler?.(ex);
}

function dispatchForbidden(ex: ApiError): void {
    if (ex.httpStatus === 403) {
        handleWAFError();
    }
    onForbiddenHandler?.(ex);
}

function handleWAFError(): void {
    const { url } = lastRequestInfo;
    if (!url) return;

    const baseUrl = url.split('?')[0];
    if (!blockedUrls.has(baseUrl)) {
        blockedUrls.add(baseUrl);
    }
}

const apiData: ApiData = {
    token: '',
    expiry: 0,
    userName: '',
};

export const api: ApiType = {
    getToken: () => apiData.token,
    getData: () => apiData,
    setData: (d: ApiData) => Object.assign(apiData, d),
    onUnauthorized: (fn?: CallbackFunc) => {
        onUnauthorizeHandler = fn ?? null;
    },
    onForbidden: (fn?: CallbackFunc) => {
        onForbiddenHandler = fn ?? null;
    },
    handleWAFError,

    post: (url: string, data?: any, headers?: any) =>
        api.call('post', `${apiConfig.baseUrl}${url}`, data, headers),

    get: (url: string, headers?: any) =>
        api.call('get', `${apiConfig.baseUrl}${url}`, undefined, headers),

    upload: (
        url: string,
        file: File,
        data?: any,
        headers?: any,
        onProgress?: (percent: number) => void
    ) => {
        const formData = new FormData();
        formData.append('file', file);
        if (data) {
            Object.entries(data).forEach(([key, value]) => {
                formData.append(key, value as string);
            });
        }
        return api.call(
            'post',
            `${apiConfig.baseUrl}${url}`,
            formData,
            { ...headers, 'Content-Type': 'multipart/form-data' },
            undefined,
            onProgress
        );
    },

    download: async (url: string, headers?: any): Promise<DownloadResponse | undefined> => {
        const handleReject = (ex: ApiError) => {
            if (ex.name === '401') {
                dispatchUnauthorized(ex);
                ex.handled = true;
            } else if (ex.name === '403') {
                dispatchForbidden(ex);
                ex.handled = true;
            }
            throw ex;
        };

        const handleResponse = (response: AxiosResponse) => {
            const contentDisposition = response.headers['content-disposition'];
            const { fileName } = parseContentDisposition(contentDisposition, 'filename');
            return { data: response.data, fileName };
        };

        try {
            const response = await axios.get(`${apiConfig.baseUrl}${url}`, {
                headers: { ...headers, authorization: `Bearer ${apiData.token}` },
                responseType: 'blob',
                withCredentials: true,
            });

            return handleResponse(response);
        } catch (ex: any) {
            if (ex.status !== 401) {
                handleReject(ex);
            }

            try {
                const refreshResult = await axios({
                    method: 'post',
                    url: `${apiConfig.accountUrl}/refresh`,
                    headers: { authorization: `Bearer ${apiData.token}` },
                    withCredentials,
                });

                const { token, expiry, userName } = refreshResult.data.result;
                apiData.token = token;
                apiData.expiry = expiry;

                if (apiData.userName !== userName) {
                    handleReject(newError('unauthorized-access', '401', 200));
                }

                const retryResponse = await axios.get(`${apiConfig.baseUrl}${url}`, {
                    headers: { ...headers, authorization: `Bearer ${apiData.token}` },
                    responseType: 'blob',
                    withCredentials,
                });

                return handleResponse(retryResponse);
            } catch (retryEx) {
                debugger;
                handleReject(retryEx as ApiError);
            }
        }
    },

    call: async (
        method: string,
        url: string,
        data?: any,
        headers?: any,
        token?: string,
        onProgress?: (percent: number) => void
    ) => {
        const handleReject = (ex: ApiError) => {
            if (ex.name === '401') {
                dispatchUnauthorized(ex);
                ex.handled = true;
            } else if (ex.name === '403') {
                dispatchForbidden(ex);
                ex.handled = true;
            }
            throw ex;
        };

        try {
            return await callAxios(method, url, data, headers, token, onProgress);
        } catch (ex: any) {
            if (ex.name !== '401') {
                handleReject(ex);
            }

            try {
                const result = await callAxios('post', `${apiConfig.accountUrl}/refresh`);
                apiData.token = result.token;
                apiData.expiry = result.expiry;

                if (apiData.userName !== result.userName) {
                    handleReject(newError('401', '401', 200));
                }

                return await callAxios(method, url, data, headers, token);
            } catch (retryEx) {
                handleReject(retryEx as ApiError);
            }
        }
    },

    directCall: async (method: string, url: string, data?: any, headers?: any, token?: string) => {
        try {
            return await callAxios(method, url, data, headers, token);
        } catch (ex: any) {
            if (ex.name === '401') {
                dispatchUnauthorized(ex);
                ex.handled = true;
            }
            if (ex.name === '403') {
                dispatchForbidden(ex);
                ex.handled = true;
            }
            throw ex;
        }
    },

    callAxios,
};
